import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { Colors, IAreaTypeResponse } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { PlaceholderMaterial } from "../../../../components/PlaceholderMaterial/PlaceholderMaterial";
import {
  CATEGORY_STATUS,
  CategoryStatus,
  IAreaTypeData,
} from "../../AreaTypes.types";

export const AreaTypePreviewTab: FC<AreaTypePreviewTabProps> = ({
  form,
  extraTabProps,
}) => {
  const originalAreaTypeDetails = extraTabProps?.originalAreaTypeDetails as
    | IAreaTypeResponse
    | undefined;
  const isEditing = !!originalAreaTypeDetails?.id;
  const {
    formState: { isDirty },
    getValues,
  } = form;

  return (
    <ConfigAccordionHeader title="Preview" color={Colors.Secondary}>
      <Box display="flex" flex={1} gap={2} width="100%">
        {!isDirty || !isEditing ? (
          <AreaTypePreview currentAreaTypeDetails={getValues()} />
        ) : (
          <>
            <Box display="flex" flexDirection="column" gap={2} flex={1}>
              <Typography color="primary" fontWeight="bold">
                Before
              </Typography>
              <AreaTypePreview
                currentAreaTypeDetails={
                  originalAreaTypeDetails as IAreaTypeData
                }
              />
            </Box>
            <Box display="flex" flexDirection="column" gap={2} flex={1}>
              <Typography color="primary" fontWeight="bold">
                After
              </Typography>
              <AreaTypePreview
                currentAreaTypeDetails={getValues()}
                originalAreaTypeDetails={originalAreaTypeDetails}
              />
            </Box>
          </>
        )}
      </Box>
    </ConfigAccordionHeader>
  );
};

export const AreaTypePreview = ({
  currentAreaTypeDetails,
  originalAreaTypeDetails,
}: {
  currentAreaTypeDetails: IAreaTypeData;
  originalAreaTypeDetails?: IAreaTypeResponse | null;
}) => {
  const { classes } = useStyles();

  const currentCategories = currentAreaTypeDetails.categories || [];

  const categoriesWithStatus = useMemo(() => {
    const originalIds = new Set(
      (originalAreaTypeDetails?.categories || []).map((c) => c.categoryId)
    );
    const currentIds = new Set(currentCategories.map((c) => c.categoryId));

    const statusOrder = {
      [CATEGORY_STATUS.ADDED]: 0,
      [CATEGORY_STATUS.EXISTING]: 1,
      [CATEGORY_STATUS.REMOVED]: 2,
    };

    return [
      ...currentCategories.map((cat) => ({
        category: cat,
        status: (originalAreaTypeDetails && !originalIds.has(cat.categoryId)
          ? CATEGORY_STATUS.ADDED
          : CATEGORY_STATUS.EXISTING) as CategoryStatus,
      })),
      ...(originalAreaTypeDetails?.categories || [])
        .filter((cat) => !currentIds.has(cat.categoryId))
        .map((cat) => ({
          category: cat,
          status: CATEGORY_STATUS.REMOVED as CategoryStatus,
        })),
    ].sort((a, b) => {
      return (
        statusOrder[a.status] - statusOrder[b.status] ||
        Number(b.category.isRequired) - Number(a.category.isRequired) ||
        a.category.displayOrder - b.category.displayOrder
      );
    });
  }, [currentCategories, originalAreaTypeDetails]);

  return (
    <Box className={classes.previewContainer}>
      <Box className={classes.previewCard}>
        <Typography className={classes.previewCardTitle}>
          {currentAreaTypeDetails.name}
        </Typography>
        <Box className={classes.placeholderContainer}>
          {categoriesWithStatus.map(({ category, status }) => (
            <PlaceholderMaterial
              key={category.categoryId}
              category={category}
              isSelected={false}
              preview
              status={status === CATEGORY_STATUS.EXISTING ? undefined : status}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

interface AreaTypePreviewTabProps {
  form: UseFormReturn<IAreaTypeData>;
  extraTabProps?: Record<string, unknown>;
}

const useStyles = makeStyles()((theme) => ({
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2),
  },
  previewCardContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    borderRadius: 8,
  },
  previewCard: {
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  previewCardTitle: {
    height: 24,
    color: theme.palette.text.primary,
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.medium,
    paddingTop: theme.spacing(1),
    margin: theme.spacing(1),
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  },
}));
